import { SidebarTheme } from "@acin.reactjs/acin-ui/sideBar";
import {
  applyIconColor,
  getMaxWidthMediaQuery,
} from "@acin.reactjs/acin-ui/theme";
import { DefaultTheme } from "styled-components";
import { hexToRgba } from "../../utils/Utils";
import { AppMediaQueries } from "./BreakpointsTheme";

const SideBarTheme = (theme: DefaultTheme): SidebarTheme => ({
  root: {
    backgroundColor: theme.palette.common.white,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.161)",
    minWidth: "85px",

    [AppMediaQueries.maxTablet]: {
      boxShadow: "none",
      height: "52px",
      padding: "0px",
      overflow: "unset",
      width: "100%",
    },
  },
  menu: {
    root: {
      "& ul": {
        padding: "0px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBlock: "0px",
        marginBottom: "1rem",
      },
    },
    item: {
      innerRoot: {
        gap: "0px",
        "& button": {
          "& > svg:first-of-type": {
            width: "auto !important",
            height: "auto !important",
          },
        },
      },
      active: {
        root: {
          backgroundColor: hexToRgba(theme.palette.primary[50], 0.3),
        },
        subMenuOpen: {
          backgroundColor: theme.palette.primary[50],
        },
        label: {
          color: theme.palette.common.black,
          fontWeight: 400,
        },
        icon: {
          "& svg path": {
            fill: "revert-layer !important",
          },
          "& path[stroke]:not([stroke='none'])": {
            stroke: theme.palette.common.black,
          },
        },
      },
      hover: {
        root: {
          backgroundColor: hexToRgba(theme.palette.primary[50], 0.3),
          paddingBottom: undefined,
          paddingTop: undefined,
        },
      },
      icon: {
        "& svg ": {
          width: "auto",
          height: "auto",
          color: theme.palette.common.black,
          fontWeight: 400,

          "& path": {
            fill: "revert-layer !important",
          },

          "& path[stroke]:not([stroke='none'])": {
            stroke: theme.palette.common.black,
          },

          [getMaxWidthMediaQuery(theme.breakpoints.md)]: {
            width: "24px",
            height: "24px",
          },
        },
      },
      label: {
        color: theme.palette.common.black,
        maxWidth: "75px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        ...theme.typography.functional.f3,

        [getMaxWidthMediaQuery(960)]: {
          maxWidth: "100%",
        },
      },
    },
  },
  subMenu: {
    root: {
      backgroundColor: theme.palette.primary[50],
      width: "170px",
    },
  },
  searchComponent: {
    optionParentLabel: {
      color: theme.palette.common.black,
    },
    optionLabel: {
      color: theme.palette.common.black,
    },
    searchIcon: {
      root: {
        color: theme.palette.common.black,
      },
    },
    selectComponent: {
      open: {
        root: {
          backgroundColor: theme.palette.common.white,
        },
      },
      dropdown: {
        root: {
          backgroundColor: theme.palette.common.white,
          borderBottom: `1px solid ${theme.palette.primary[700]}`,
          borderLeft: `1px solid ${theme.palette.primary[700]}`,
          borderRight: `1px solid ${theme.palette.primary[700]}`,
        },
        option: {
          root: {
            color: theme.palette.common.black,
          },
        },
      },
      root: {
        backgroundColor: theme.palette.primary[100],
        marginTop: "12px",
        borderRadius: "4px",
      },
      input: {
        backgroundColor: theme.palette.primary[100],
      },
      inputContainer: {
        backgroundColor: theme.palette.primary[100],
        border: "none",

        "& svg path": {
          stroke: `${theme.palette.common.black} !important`,
        },
      },
    },
  },
  expanded: {
    root: {
      minWidth: "220px",
    },
    topComponent: {
      position: "relative",
      left: "0px",
    },
    searchComponent: {
      selectComponent: {
        root: {
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.primary[700]}`,
        },
        hover: {
          root: {
            backgroundColor: theme.palette.common.white,
          },
          dropdown: {
            option: {
              root: {
                "&:hover": {
                  backgroundColor: theme.palette.primary[50],
                },
              },
            },
          },
        },
        open: {
          root: {
            borderBottom: 0,
          },
        },
        input: {
          backgroundColor: "transparent",
          color: theme.palette.common.black,
        },
        inputContainer: {
          backgroundColor: `${theme.palette.common.white} !important`,

          "& svg path": {
            stroke: `${theme.palette.common.black} !important`,
          },

          "& input::placeholder": {
            color: theme.palette.common.black,
          },
        },
        dropdown: {
          root: {
            boxShadow: "none",
            marginTop: "-4px",
            backgroundColor: theme.palette.common.white,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderTop: `1px solid ${theme.palette.primary[700]} !important`,
          },
        },
      },
      searchIcon: {
        root: {
          backgroundColor: "transparent",
        },
      },
    },
    menu: {
      item: {
        active: {
          root: {
            backgroundColor: theme.palette.primary[50],
            borderLeft: 0,
          },
          label: {
            color: theme.palette.common.black,
            fontWeight: 400,
            marginLeft: 0,
            maxWidth: "100%",
          },
          innerRoot: {
            width: "100%",
          },
        },
        hover: {
          root: {
            backgroundColor: hexToRgba(theme.palette.primary[50], 0.3),
          },
        },
        innerRoot: {
          justifyContent: "flex-start",
        },
      },
    },
    helpComponent: {
      root: {
        padding: "1rem 0.5rem",
      },
    },
  },
  helpComponent: {
    label: {
      root: {
        color: theme.palette.common.black,
      },
    },
    button: {
      root: {
        backgroundColor: theme.palette.secondary[50],
        "& path[stroke]:not([stroke='none'])": {
          stroke: theme.palette.secondary[600],
        },
      },
      hover: {
        root: {
          backgroundColor: theme.palette.secondary[50],
        },
      },
      focused: {
        root: {
          backgroundColor: theme.palette.secondary[50],
        },
      },
    },
  },
  expandComponent: {
    icon: {
      icon: applyIconColor(theme.palette.common.black),
    },
    root: {
      borderTop: `1px solid ${theme.palette.greys[100]}`,
    },
  },
  topComponent: {
    paddingLeft: "0px",
    position: "relative",
    left: "-10px",
    "& svg": {
      width: "auto !important",
      height: "40px !important",

      [getMaxWidthMediaQuery(990)]: {
        height: "32px !important",
      },
    },

    [getMaxWidthMediaQuery(959)]: {
      paddingLeft: "0px",
    },
  },
  sidePane: {
    root: {
      background: theme.palette.common.white,
      paddingTop: "16px",

      "& path[stroke]:not([stroke='none'])": {
        stroke: theme.palette.common.black,
      },

      "& span, p": {
        color: theme.palette.common.black,
      },

      "& button": {
        paddingLeft: 0,
      },
    },
  },
  genericHelpComponent: {
    root: {
      [getMaxWidthMediaQuery(theme.breakpoints.md)]: {
        "& svg": {
          width: "20px",
          height: "20px",
        },
      },
    },
    contact: {
      list: {
        item: {
          root: {
            "& svg path[stroke]:not([stroke='none'])": {
              stroke: theme.palette.primary[700],
            },
          },
          title: {
            textDecoration: "none",
            color: `${theme.palette.common.black} !important`,

            "&:hover": {
              textDecoration: "underline",
            },
          },
        },
      },
    },
    navigation: {
      list: {
        item: {
          root: {
            [getMaxWidthMediaQuery(theme.breakpoints.md)]: {
              borderColor: theme.palette.primary[600],
            },
          },
          link: {
            [getMaxWidthMediaQuery(theme.breakpoints.md)]: {
              color: theme.palette.common.black,
            },
          },
        },
      },
    },
  },
});

export default SideBarTheme;
